import type {
  PermissionActionFilterInput,
  PermissionDecision,
  PermissionEvaluationInput,
  PermissionRequirement,
  PermissionSnapshot,
  PermissionSurface,
} from '../types/permissions'
import type { PermissionAwareActionItem } from './types'
import { normalizeAllowedActions } from './normalize'

const allowedDecision: PermissionDecision = {
  allowed: true,
  outcome: 'allowed',
  reason: 'ready',
}

const permissionAliases: Record<string, string[]> = {
  view: ['view', 'list', 'read', 'retrieve', 'detail', 'show'],
  create: ['create', 'add', 'upload', 'import', 'invite', 'associate'],
  update: ['update', 'edit', 'rename', 'change', 'save', 'set'],
  delete: ['delete', 'remove', 'destroy', 'dissociate', 'cancel'],
  select: ['select', 'activate', 'set_active'],
  pay: ['pay', 'add_credit'],
  download: ['download', 'openrc', 'yaml'],
  restore: ['restore', 'revert'],
  retry: ['retry', 'rerun'],
  abort: ['abort', 'cancel'],
}

const normalizeToken = (token: string): string =>
  token
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

const asTokenList = (value?: PermissionRequirement): string[] => {
  if (!value) {
    return []
  }

  return (Array.isArray(value) ? value : [value]).map(normalizeToken).filter(Boolean)
}

const expandPermissionRequirement = (value?: PermissionRequirement): string[] =>
  asTokenList(value).flatMap((token) => Array.from(new Set([token, ...(permissionAliases[token] || [])].map(normalizeToken))))

const getCandidateFragments = (token: string): string[] => {
  const fragments = token.split(/[._]/g).filter(Boolean)

  return Array.from(new Set([token, ...fragments]))
}

const matchesRequiredToken = (required: string, candidate: string): boolean => {
  if (required === candidate) {
    return true
  }

  // Namespaced permissions must match exactly. Fragment matching is only for
  // generic requirements and short action aliases such as view/create/update.
  if (required.split('_').length >= 3) {
    return false
  }

  const requiredFragments = getCandidateFragments(required)
  const candidateFragments = getCandidateFragments(candidate)

  return requiredFragments.some((fragment) => candidateFragments.includes(fragment))
}

const hasMatchingRequirement = (requirement: PermissionRequirement | undefined, sourceTokens: string[]): boolean => {
  const requiredTokens = expandPermissionRequirement(requirement)

  if (requiredTokens.length === 0) {
    return true
  }

  const normalizedSourceTokens = sourceTokens.map(normalizeToken).filter(Boolean)

  return requiredTokens.some((requiredToken) =>
    normalizedSourceTokens.some((sourceToken) => matchesRequiredToken(requiredToken, sourceToken)),
  )
}

const resolvePermissionEntry = (
  permissions: PermissionSnapshot['entityPermissions'][string],
  requirement?: PermissionRequirement,
): boolean | null => {
  const requiredTokens = expandPermissionRequirement(requirement)

  if (requiredTokens.length === 0) {
    return true
  }

  const entries = Object.entries(permissions || {})

  if (entries.length === 0) {
    return null
  }

  const matchedEntries = entries.filter(([permissionKey]) =>
    requiredTokens.some((requiredToken) => matchesRequiredToken(requiredToken, normalizeToken(permissionKey))),
  )

  if (matchedEntries.length === 0) {
    return null
  }

  return matchedEntries.some(([, allowed]) => allowed)
}

const getDeniedOutcome = (surface: PermissionSurface): PermissionDecision['outcome'] => {
  if (surface.kind === 'route') {
    return surface.denyMode === 'render_access_denied' ? 'render_access_denied' : 'redirect_403'
  }

  return 'hidden'
}

export const createDeniedDecision = (
  surface: PermissionSurface,
  reason: PermissionDecision['reason'],
): PermissionDecision => ({
  allowed: false,
  outcome: getDeniedOutcome(surface),
  reason,
})

export const evaluatePermissionSurface = (
  snapshot: PermissionSnapshot,
  surface: PermissionSurface,
  input?: PermissionEvaluationInput,
): PermissionDecision => {
  if (snapshot.status !== 'ready') {
    return createDeniedDecision(surface, 'snapshot_not_ready')
  }

  if (!hasMatchingRequirement(surface.requiredFeature, snapshot.appMenuFeatures)) {
    return createDeniedDecision(surface, 'feature_missing')
  }

  if (surface.requiredPermission) {
    const permissions = snapshot.entityPermissions[surface.entityKey] || {}

    const permissionResult = resolvePermissionEntry(permissions, surface.requiredPermission)

    if (permissionResult === null && !surface.allowMissingPermission) {
      return createDeniedDecision(surface, 'permission_missing')
    }

    if (permissionResult === false) {
      return createDeniedDecision(surface, 'permission_false')
    }
  }

  if (surface.requiredAllowedAction && input?.itemAllowedActions !== undefined && input?.itemAllowedActions !== null) {
    const allowedActions = normalizeAllowedActions(input?.itemAllowedActions)

    if (!hasMatchingRequirement(surface.requiredAllowedAction, allowedActions)) {
      return createDeniedDecision(surface, 'allowed_action_missing')
    }
  }

  if (surface.extraGuards?.some((guardKey) => !input?.extraGuards?.[guardKey])) {
    return createDeniedDecision(surface, 'extra_guard_failed')
  }

  return allowedDecision
}

export const isPermissionDecisionAllowed = (decision?: PermissionDecision | null): boolean =>
  Boolean(decision?.allowed)

export const filterPermissionAwareActions = <T extends PermissionAwareActionItem>(
  snapshot: PermissionSnapshot,
  items: T[] = [],
  input?: PermissionActionFilterInput,
): T[] =>
  items.filter((item) => {
    if (item.hidden) {
      return false
    }

    if (item.permissionDecision) {
      return item.permissionDecision.allowed
    }

    if (!item.permissionSurface) {
      return true
    }

    const decision = evaluatePermissionSurface(snapshot, item.permissionSurface, {
      itemAllowedActions: item.allowedActions ?? input?.itemAllowedActions,
      extraGuards: item.extraGuards ?? input?.extraGuards,
    })

    return decision.allowed
  })
